var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getPageLoginState, getTimePartingInfo, omitProperties, recursiveMerge, } from "./CommonHelper";
import { defaultGlobalPageProperties as defaultSegmentGlobalPageProperties, defaultVideoProperties as defaultSegmentVideoProperties, getDeviceIdCookie as getSegmentDeviceIdCookie, getPageContentLevels as getSegmentPageContentLevels, } from "./SegmentHelper";
import { NewRelicErrors, NewRelicEvents } from "./Enums";
/**
 * ### getDefaultEventProps
 * returns default props for New Relic events
 * @param {AnyObject} props - an object that will be recursively merged in with other default
 * properties
 */
export var getDefaultEventProps = function (props) {
    var _a, _b, _c, _d, _e;
    var account = (_a = window.DataLayer) === null || _a === void 0 ? void 0 : _a.getAccount();
    var anonymousAccount = (_b = window.DataLayer) === null || _b === void 0 ? void 0 : _b.getAnonymousAccount();
    var pageLoginState = getPageLoginState(account);
    var mergedProps = recursiveMerge(defaultSegmentGlobalPageProperties, props, __assign(__assign(__assign({}, getSegmentPageContentLevels(props)), getTimePartingInfo()), { authentication_status: !!account, device_id: getSegmentDeviceIdCookie(), dma: (account === null || account === void 0 ? void 0 : account.dma) || (anonymousAccount === null || anonymousAccount === void 0 ? void 0 : anonymousAccount.dma), event_type: "app", mvpd: (_c = window.DataLayer) === null || _c === void 0 ? void 0 : _c.getMVPD(), page_type: (_e = (_d = window.utag) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.page_type, page_login_state: pageLoginState, profile_id: (account === null || account === void 0 ? void 0 : account.profileId) || (anonymousAccount === null || anonymousAccount === void 0 ? void 0 : anonymousAccount.profileId) }));
    return mergedProps;
};
/**
 * ### getNewRelicPageName
 * returns a string representing the page name that the New Relic event occurred on
 * @param {Nullable<string>} pathname - nullable string representing the path name
 * @param {{
    showId: string,
    movieId: string,
    articleId: string,
  }} routeParams - object containing parameters that help indicate the current route
 */
export var getNewRelicPageName = function (pathname, routeParams) {
    var _a, _b;
    var path = pathname !== null && pathname !== void 0 ? pathname : "";
    if (path === "/" || path === "/featured")
        return "Main - Home";
    if (routeParams === null || routeParams === void 0 ? void 0 : routeParams.showId)
        return "Detail";
    if (routeParams === null || routeParams === void 0 ? void 0 : routeParams.movieId)
        return "Detail";
    if (routeParams === null || routeParams === void 0 ? void 0 : routeParams.articleId)
        return "Article";
    if (path.includes("/news/"))
        return "Main - News";
    if (path.includes("/sports/"))
        return "Main - Sports";
    if (path.includes("/entertainment/"))
        return "Main - Entertainment";
    if (path.includes("/account/"))
        return "Main - Account";
    if (path.includes("/live/"))
        return "EPG";
    if (path.includes("/watch/"))
        return "VOD";
    if (path.includes("/local/"))
        return "Main - Local";
    if (path.includes("/shows/"))
        return "Main - Shows";
    if (path.includes("/personalities/"))
        return "Main - Personalities";
    if (path.includes("/listen/"))
        return "Main - Listen";
    if (path.includes("/live-news/"))
        return "Main - Live News";
    if (path.includes("/find/"))
        return "Search";
    if (path.includes("/channel/tucker-carlson-channel/"))
        return "Main - Tucker Carlson";
    if (path.includes("/channel/")) {
        var channelName = ((_a = path === null || path === void 0 ? void 0 : path.split("/")) === null || _a === void 0 ? void 0 : _a.filter(function (s) { return s; })[1]) || "";
        return "Main - " + ((_b = channelName === null || channelName === void 0 ? void 0 : channelName.charAt(0)) === null || _b === void 0 ? void 0 : _b.toUpperCase) + channelName.slice(1);
    }
    return "Unknown Path";
};
/**
 * ### newRelicError
 * sends a New Relic error using newrelic.addPageAction()
 * @param {Error} error - error object representing the error that occurred
 * @param {AnyObject} props - payload of properties to be sent to New Relic
 */
export var newRelicError = function (error, props) {
    if (error === void 0) { error = {}; }
    if (props === void 0) { props = {}; }
    var message = error.message, name = error.name, response = error.response, stack = error.stack, type = error.type;
    var pageName = props.pageName;
    var newrelic = window.newrelic;
    if (!newrelic)
        return;
    var errorName = NewRelicErrors[type || "generalError"];
    var trackingProps = omitProperties(getDefaultEventProps(props), [
        "account",
    ]);
    newrelic.addPageAction(errorName, __assign(__assign({}, trackingProps), { errorMessage: message || "Unknown Error", errorServerResponse: response || "", errorStack: stack || "", errorTitle: name || "", pageName: pageName }));
};
/**
 * ### newRelicEvent
 * sends a New Relic event using newrelic.addPageAction()
 * @param {string} event - name of the event
 * @param props - payload of properties to be sent to New Relic
 */
export var newRelicEvent = function (event, props) {
    if (props === void 0) { props = {}; }
    var newrelic = window.newrelic;
    if (!newrelic)
        return;
    var eventName = NewRelicEvents[event];
    var trackingProps = omitProperties(getDefaultEventProps(props), [
        "account",
    ]);
    newrelic.addPageAction(eventName, trackingProps);
};
/**
 * ### playerNewRelicError
 * sends a New Relic player error using newrelic.addPageAction()
 * @param {Error} error - error object representing the error that occurred
 * @param {AnyObject} props - payload of properties to be sent to New Relic
 */
export var playerNewRelicError = function (error, props) {
    if (props === void 0) { props = {}; }
    var playerProps = recursiveMerge(defaultSegmentVideoProperties, props);
    newRelicError(error, playerProps);
};
/**
 * ### playerNewRelicEvent
 * sends a New Relic player event using newrelic.addPageAction()
 * @param {string} event - name of the event
 * @param props - payload of properties to be sent to New Relic
 */
export var playerNewRelicEvent = function (event, props) {
    if (props === void 0) { props = {}; }
    var playerProps = recursiveMerge(defaultSegmentVideoProperties, props);
    newRelicEvent(event, playerProps);
};
